import React, { useState } from "react"

import useEmailVerification from "../EmailVerification/useEmailVerification"
import { useCurrentUser } from "../context/user/UserContext"
import { useHistory } from "react-router"
import VerificationSentModal from "./VerificationSentModal"
import { reportErrorMessage } from "#/utils/ErrorReporting"
import { FormattedMessage, useIntl } from "react-intl"
import EmployeeOnlyModalContainer from "../employee-only-containers/EmployeeOnlyModalContainer"
import { PiiText } from "../../services/tracking"
import { Button, DialogContentText, Link, Typography } from "@carrotfertility/carotene-core"
import illoEnvelopeAt from "./assets/images/illo-envelope-at.png"
import illoEnvelopeAt2x from "./assets/images/illo-envelope-at-2x.png"
import { DialogTitleImage, InfoDialog } from "@carrotfertility/carotene-core-x"
import { EmployeeSupportUrl } from "#/utils/EmployeeSupportLink"

export function MessagingEmailVerificationModal() {
  const { personalEmail, employeeId } = useCurrentUser()
  const { sendVerificationEmail } = useEmailVerification()
  const [open, setOpen] = useState(true)
  const [verificationEmailSent, setVerificationEmailSent] = useState(false)
  const [disableSendButton, setDisableSendButton] = useState(false)
  const [error, setError] = useState(false)
  const history = useHistory()

  const onClose = (): void => {
    history.push({
      state: { showVerifyEmailModal: false }
    })
    setOpen(false)
    setError(false)
    setVerificationEmailSent(false)
  }
  const onClick = async (): Promise<void> => {
    try {
      setDisableSendButton(true)
      await sendVerificationEmail()
      setVerificationEmailSent(true)
      setDisableSendButton(false)
    } catch (error) {
      setError(true)
      reportErrorMessage(`Unable to send verification email for employee id: ${employeeId}`)
    }
  }

  if (verificationEmailSent) {
    return <VerificationSentModal onClose={onClose} open={open} />
  }

  return (
    <EmployeeOnlyModalContainer onClose={onClose} open={open}>
      {error ? (
        <ErrorDialog onClose={onClose} open={open} />
      ) : (
        <VerifyEmailDialog
          personalEmail={personalEmail}
          onClose={onClose}
          onClick={onClick}
          disableSendButton={disableSendButton}
          open={open}
        />
      )}
    </EmployeeOnlyModalContainer>
  )
}

function ErrorDialog({ onClose, open }: { onClose: () => void; open: boolean }) {
  const intl = useIntl()
  return (
    <InfoDialog
      onClose={onClose}
      open={open}
      title={intl.formatMessage({ defaultMessage: "Something's not working" })}
      primaryAction={
        <Button onClick={onClose}>
          <FormattedMessage defaultMessage="Close" />
        </Button>
      }
    >
      <DialogContentText>
        <FormattedMessage
          defaultMessage="Try reloading this page. If the problem persists, feel free to <link>contact us</link>."
          values={{
            link: (linkContent) => (
              <Link href={EmployeeSupportUrl} target={"_blank"}>
                {linkContent}
              </Link>
            )
          }}
        />
      </DialogContentText>
    </InfoDialog>
  )
}

function VerifyEmailDialog({
  personalEmail,
  onClose,
  onClick,
  disableSendButton,
  open
}: {
  personalEmail: string
  onClose: () => void
  onClick: () => Promise<void>
  disableSendButton: boolean
  open: boolean
}) {
  const intl = useIntl()
  return (
    <InfoDialog
      slotProps={{
        closeButton: {
          "aria-label": "Close"
        }
      }}
      image={
        <DialogTitleImage width="100%" maxWidth="148px" src={illoEnvelopeAt} srcSet={`${illoEnvelopeAt2x} 2x`} alt="" />
      }
      title={intl.formatMessage({ defaultMessage: "Verify your email address" })}
      primaryAction={
        <Button onClick={onClick} disabled={disableSendButton}>
          <FormattedMessage defaultMessage="Send verification email" />
        </Button>
      }
      open={open}
      onClose={onClose}
    >
      <DialogContentText>
        <FormattedMessage
          defaultMessage="Please check <bold>{personalEmail}</bold> and click the verification link to access your messages."
          values={{
            personalEmail: <PiiText>{personalEmail}</PiiText>,
            bold: (boldContent) => (
              <Typography component="span" fontSize="inherit" fontWeight="bold">
                {boldContent}
              </Typography>
            )
          }}
        />
      </DialogContentText>
    </InfoDialog>
  )
}
